import {useCallback} from 'react'
import {View} from 'react-native'
import * as Clipboard from 'expo-clipboard'
import {plural} from '@lingui/core/macro'
import {useLingui} from '@lingui/react/macro'
import {useNavigation} from '@react-navigation/native'

import {HITSLOP_20} from '#/lib/constants'
import {type NavigationProp} from '#/lib/routes/types'
import {
  type Classroom,
  classroomInvitationLink,
  type ClassroomMenuItem,
  classroomMenuItems,
  findMember,
  hasRequested,
  kindOfAccountType,
  type OwnerKind,
  readOwnerKind,
  useClassroomActor,
} from '#/state/classrooms'
import {
  type ClassroomViewer,
  useCancelRequestMutation,
  useDeleteClassroomMutation,
  useLeaveClassroomMutation,
} from '#/state/queries/classrooms'
import {useProfileQuery} from '#/state/queries/profile'
import {useRequireAuth, useSession} from '#/state/session'
import {EventStopper} from '#/view/com/util/EventStopper'
import {atoms as a} from '#/alf'
import {Button, ButtonIcon, ButtonText} from '#/components/Button'
import {ownerKindIcon} from '#/components/classrooms/ownerKindIcon'
import {useDialogControl} from '#/components/Dialog'
import {ChainLink_Stroke2_Corner0_Rounded as ChainLinkIcon} from '#/components/icons/ChainLink'
import {DotGrid3x1_Stroke2_Corner0_Rounded as Ellipsis} from '#/components/icons/DotGrid'
import {Lock_Stroke2_Corner0_Rounded as LockIcon} from '#/components/icons/Lock'
import {
  PersonCheck_Stroke2_Corner0_Rounded as PersonCheckIcon,
  PersonPlus_Stroke2_Corner0_Rounded as PersonPlusIcon,
} from '#/components/icons/Person'
import {SettingsGear2_Stroke2_Corner0_Rounded as SettingsIcon} from '#/components/icons/SettingsGear2'
import {Trash_Stroke2_Corner0_Rounded as TrashIcon} from '#/components/icons/Trash'
import * as Menu from '#/components/Menu'
import * as Prompt from '#/components/Prompt'
import * as Toast from '#/components/Toast'
import {InviteDialog, RequestsDialog} from './AdminDialogs'
import {ClassroomSettingsDialog} from './ClassroomSettingsDialog'
import {EditClassroomDialog} from './EditClassroomDialog'
import {AskToJoinDialog, JoinByCodeDialog} from './JoinDialogs'
import {noRequestMessage, showClassroomError} from './showClassroomError'

/**
 * What you can do about a classroom, in the shape a profile uses.
 *
 * A profile puts one pill — the thing you came to do — beside a round button
 * that holds everything else, and the classroom keeps that shape on purpose: the
 * same row, in the same place (above the name), with the same sizes. One action
 * you can take is named; the rest are one tap away, each with its icon, and the
 * rare and destructive ones (leaving, deleting) live in there rather than
 * competing with the work.
 *
 * What the pill says is the whole of the difference between the four people who
 * arrive here. The school that runs the room edits it. A teacher invites. A
 * student, a parent and a visitor are readers until they ask to join — so their
 * one named action is the way in, or nothing at all once they are in.
 */
export function ClassroomHeaderActions({
  classroom,
  viewer,
  inviteCode,
}: {
  classroom: Classroom
  /** What the server says the reader may do here. */
  viewer: ClassroomViewer
  /** The code the invitation link that opened this page carried. */
  inviteCode?: string
}) {
  const {t: l, i18n} = useLingui()
  const {hasSession} = useSession()
  const actor = useClassroomActor()
  const deleteMutation = useDeleteClassroomMutation(classroom.id)
  const leaveMutation = useLeaveClassroomMutation(classroom.id)
  const cancelRequestMutation = useCancelRequestMutation(classroom.id)
  const requireAuth = useRequireAuth()
  const navigation = useNavigation<NavigationProp>()

  const editControl = useDialogControl()
  const inviteControl = useDialogControl()
  const requestsControl = useDialogControl()
  const settingsControl = useDialogControl()
  const askControl = useDialogControl()
  const joinCodeControl = useDialogControl()
  const deleteControl = Prompt.usePromptControl()
  const leaveControl = Prompt.usePromptControl()
  const cancelRequestControl = Prompt.usePromptControl()

  const member = findMember(classroom, actor?.did)
  /*
   * Two different questions, and the room tells them apart: the school *owns*
   * the classroom, while a moderator it named *runs* it — the requests, the
   * settings and the door — without the one act that cannot be taken back.
   */
  const canManage = viewer.canManage
  const isOwner = !!actor && actor.did === classroom.ownerDid
  const canInvite = viewer.canInvite
  /** The seats that may answer somebody's ask, whoever they are for. */
  const mayAnswerRequests = canManage || member?.role === 'teacher'
  const requested = hasRequested(classroom, actor?.did)

  /*
   * Who the account that runs the room is, for the one row about it.
   *
   * The owner's own profile is the truth — the kind it answered at signup and
   * the refinement after it (`readOwnerKind`) — and the room's own record is the
   * fallback for the moment before that profile arrives, and for a deployment
   * that does not hand the fields back at all: the room kept the kind of account
   * that made it (`Classroom.ownerAccountType`), which is enough to name the kind.
   *
   * Nothing at all is a real answer, and it is the one the row is not drawn for: a
   * room whose owner the app cannot name has no row to misname ({@link OwnerKind}).
   */
  const {data: ownerProfile} = useProfileQuery({did: classroom.ownerDid})
  const ownerKind =
    readOwnerKind(ownerProfile) ?? kindOfAccountType(classroom.ownerAccountType)

  /**
   * The rows the menu is made of (`classroomMenuItems`), decided here and drawn
   * there: the pill above is for the four people who arrive, and the menu is
   * everything else each of them may do.
   */
  const menuItems = classroomMenuItems(classroom, actor, !!ownerKind)

  /* The same link the invitation dialog draws as a QR, code and all. */
  const onCopyLink = useCallback(() => {
    void Clipboard.setStringAsync(classroomInvitationLink(classroom))
    Toast.show(l`Classroom link copied`)
  }, [classroom, l])

  const onLeave = (reason: 'manual' | 'next-level') => {
    leaveMutation.mutate(undefined, {
      onSuccess: () => {
        Toast.show(
          reason === 'next-level'
            ? l`You left ${classroom.name} for your next level`
            : l`You left ${classroom.name}`,
        )
      },
      onError: error => showClassroomError(i18n, error),
    })
  }

  const onCancelRequest = () => {
    cancelRequestMutation.mutate(undefined, {
      onSuccess: ({alreadyHandled}) =>
        Toast.show(
          alreadyHandled ? noRequestMessage(i18n) : l`Join request rescinded.`,
        ),
      onError: error => showClassroomError(i18n, error),
    })
  }

  const onDelete = useCallback(() => {
    if (!actor) return
    deleteMutation.mutate(undefined, {
      onSuccess: () => {
        Toast.show(l`${classroom.name} deleted`)
        // The screen we are on is the thing that no longer exists, so leave it
        // rather than render its not-found state behind the dialog.
        navigation.navigate('Profile', {name: actor.handle})
      },
      onError: error => showClassroomError(i18n, error),
    })
  }, [actor, classroom.name, deleteMutation, i18n, l, navigation])

  /** The one named action, which is where the four kinds of visitor differ. */
  let pill: React.ReactNode = null
  if (!hasSession) {
    pill = (
      <Button
        testID="classroomSignInToJoin"
        label={l`Sign in to join`}
        color="primary"
        size="small"
        onPress={() => requireAuth(async () => {})}>
        <ButtonText>{l`Sign in to join`}</ButtonText>
      </Button>
    )
  } else if (canManage) {
    pill = (
      <Button
        testID="classroomEditButton"
        label={l`Edit ${classroom.name}`}
        color="secondary"
        size="small"
        onPress={() => editControl.open()}>
        <ButtonText>{l`Edit classroom`}</ButtonText>
      </Button>
    )
  } else if (member) {
    // In the room already: a reader's named action is not "leave", which is in
    // the menu with the other exits. A teacher's is handing out the code.
    pill = canInvite ? (
      <Button
        testID="classroomInvitePill"
        label={l`Invite people to ${classroom.name}`}
        color="secondary"
        size="small"
        onPress={() => inviteControl.open()}>
        <ButtonIcon icon={PersonPlusIcon} />
        <ButtonText>{l`Invite`}</ButtonText>
      </Button>
    ) : null
  } else if (requested) {
    /*
     * *Request pending* is also the way to take the ask back.
     *
     * The pill used to be a dead label: the person could see that the school
     * had not answered and had no way to stop waiting. An ask that can be sent
     * and not unsent is a trap, so the pill opens the withdrawal instead of
     * announcing itself.
     */
    pill = (
      <Button
        testID="classroomRequestPending"
        label={l`Request pending`}
        color="secondary"
        size="small"
        onPress={() => cancelRequestControl.open()}>
        <ButtonText>{l`Request pending`}</ButtonText>
      </Button>
    )
  } else if (classroom.visibility === 'public') {
    /*
     * A public classroom is one anybody may *ask* to be let into: it can be
     * found, so the door is visible, and the school still decides who comes in.
     */
    pill = (
      <Button
        testID="classroomAskToJoinButton"
        label={l`Ask to join ${classroom.name}`}
        color="primary"
        size="small"
        disabled={!actor}
        onPress={() => askControl.open()}>
        <ButtonText>{l`Ask to join`}</ButtonText>
      </Button>
    )
  } else {
    /*
     * A private classroom cannot be found, so there is nobody to ask: the code
     * the school handed out is the only way in, and the pill points at it
     * rather than at a request the room would never answer.
     */
    pill = (
      <Button
        testID="classroomJoinWithCodeButton"
        label={l`Join ${classroom.name} with a code`}
        color="primary"
        size="small"
        onPress={() => joinCodeControl.open()}>
        <ButtonText>{l`Join with a code`}</ButtonText>
      </Button>
    )
  }

  return (
    <EventStopper onKeyDown={false}>
      <View style={[a.flex_row, a.align_center, a.justify_end, a.gap_xs]}>
        {pill}
        {/*
          The round button is the menu, so a menu with nothing in it is not
          drawn at all: a private room's visitor who has asked is offered no
          code, no link and no ask (`classroomMenuItems`), and the pill that
          says *Request pending* is the whole of their page.
        */}
        {menuItems.length > 0 ? (
          <ClassroomMenu
            classroom={classroom}
            items={menuItems}
            ownerKind={ownerKind}
            onCopyLink={onCopyLink}
            onDelete={() => deleteControl.open()}
            onLeave={() => leaveControl.open()}
            inviteControl={inviteControl}
            requestsControl={requestsControl}
            settingsControl={settingsControl}
            askControl={askControl}
            joinCodeControl={joinCodeControl}
          />
        ) : null}
      </View>

      {/* One set of dialogs per classroom page, opened from the pill or the menu. */}
      {canManage ? (
        <>
          <EditClassroomDialog
            control={editControl}
            classroom={classroom}
            viewer={viewer}
          />
          <ClassroomSettingsDialog
            control={settingsControl}
            classroom={classroom}
            viewer={viewer}
          />
        </>
      ) : null}

      {mayAnswerRequests ? (
        <RequestsDialog
          control={requestsControl}
          classroom={classroom}
          viewer={viewer}
        />
      ) : null}

      {/*
        Deleting the room is the school's own, and only its own: a moderator
        runs the classroom, it does not get to end it.
      */}
      {isOwner ? (
        <Prompt.Outer control={deleteControl}>
          <Prompt.Content>
            <Prompt.TitleText>{l`Delete ${classroom.name}?`}</Prompt.TitleText>
            <Prompt.DescriptionText>
              {l`Everything posted in it goes with it, for everyone in the classroom. This cannot be undone.`}
            </Prompt.DescriptionText>
          </Prompt.Content>
          <Prompt.Actions>
            <Prompt.Action
              testID="classroomDeleteConfirm"
              cta={l`Delete classroom`}
              color="negative"
              onPress={onDelete}
            />
            <Prompt.Cancel />
          </Prompt.Actions>
        </Prompt.Outer>
      ) : null}

      {requested && !member ? (
        <Prompt.Outer control={cancelRequestControl}>
          <Prompt.Content>
            <Prompt.TitleText>
              {l`Cancel your request to join ${classroom.name}?`}
            </Prompt.TitleText>
            <Prompt.DescriptionText>
              {l`The classroom will no longer see your request. You can send it again at any time.`}
            </Prompt.DescriptionText>
          </Prompt.Content>
          <Prompt.Actions>
            <Prompt.Action
              testID="classroomCancelRequestConfirm"
              cta={l`Cancel request`}
              color="negative"
              onPress={onCancelRequest}
            />
            {/*
              Both buttons used to read *Cancel*: one to stop waiting, one to
              leave the dialog alone, side by side and indistinguishable. The
              dismiss says what dismissing it means instead.
            */}
            <Prompt.Cancel cta={l`Keep waiting`} />
          </Prompt.Actions>
        </Prompt.Outer>
      ) : null}

      {canInvite ? (
        <InviteDialog control={inviteControl} classroom={classroom} />
      ) : null}

      {member && !isOwner ? (
        <Prompt.Outer control={leaveControl}>
          <Prompt.Content>
            <Prompt.TitleText>{l`Leave this classroom?`}</Prompt.TitleText>
            {/*
              How you get back in is the door this room actually has. A public
              classroom is asked into — telling an ex-member to come back *with the
              code* names a door that classroom's own page does not offer, and one
              the school was never given the chance to hand out.
            */}
            <Prompt.DescriptionText>
              {classroom.visibility === 'public'
                ? member.role === 'student'
                  ? l`Your parent's seat leaves with you. You can ask to join again.`
                  : l`You can ask to join again.`
                : member.role === 'student'
                  ? l`Your parent's seat leaves with you. You can join again with the code the school gave you.`
                  : l`You can join again with the code the school gave you.`}
            </Prompt.DescriptionText>
          </Prompt.Content>
          <Prompt.Actions>
            {member.role === 'student' ? (
              <Prompt.Action
                cta={l`I moved to the next level`}
                onPress={() => onLeave('next-level')}
              />
            ) : null}
            <Prompt.Action
              cta={l`Leave`}
              color="secondary"
              onPress={() => onLeave('manual')}
            />
            <Prompt.Cancel />
          </Prompt.Actions>
        </Prompt.Outer>
      ) : null}

      {/*
        Both doors get the invitation's code, so a visitor who came by the link
        joins with it instead of typing it again. They stay mounted once the
        visitor is in, so the dialog that seated them can still say so and
        offer the room.
      */}
      <AskToJoinDialog
        control={askControl}
        classroom={classroom}
        code={inviteCode}
      />
      <JoinByCodeDialog
        control={joinCodeControl}
        classroom={classroom}
        code={inviteCode}
      />
    </EventStopper>
  )
}

/**
 * The round button. The profile's own menu, with the classroom's own items:
 * named actions with icons, grouped, and the destructive ones separated at the
 * bottom by a divider.
 */
function ClassroomMenu({
  classroom,
  items,
  ownerKind,
  onCopyLink,
  onDelete,
  onLeave,
  inviteControl,
  requestsControl,
  settingsControl,
  askControl,
  joinCodeControl,
}: {
  classroom: Classroom
  /** What this menu offers this reader, as `classroomMenuItems` answers it. */
  items: ClassroomMenuItem[]
  /**
   * Who runs the room, when the app can say (`readOwnerKind`): the words of the
   * row that opens their page, and which of the two drawings it wears. `null` is
   * a room whose owner this device has not been told about, and the row is not
   * drawn for it.
   */
  ownerKind: OwnerKind | null
  onCopyLink: () => void
  onDelete: () => void
  onLeave: () => void
  inviteControl: ReturnType<typeof useDialogControl>
  requestsControl: ReturnType<typeof useDialogControl>
  settingsControl: ReturnType<typeof useDialogControl>
  askControl: ReturnType<typeof useDialogControl>
  joinCodeControl: ReturnType<typeof useDialogControl>
}) {
  const {t: l} = useLingui()
  const navigation = useNavigation<NavigationProp>()
  const requestCount = classroom.requests.length

  return (
    <Menu.Root>
      <Menu.Trigger label={l`More options`}>
        {({props}) => (
          <Button
            {...props}
            testID="classroomHeaderDropdownBtn"
            label={l`More options`}
            hitSlop={HITSLOP_20}
            variant="solid"
            color="secondary"
            size="small"
            shape="round">
            <ButtonIcon icon={Ellipsis} size="sm" />
          </Button>
        )}
      </Menu.Trigger>

      <Menu.Outer style={{minWidth: 200}}>
        <Menu.Group>
          {items.includes('invite') ? (
            <Menu.Item
              testID="classroomMenuInvite"
              label={l`Invite people`}
              onPress={() => inviteControl.open()}>
              <Menu.ItemText>{l`Invite people`}</Menu.ItemText>
              <Menu.ItemIcon icon={PersonPlusIcon} />
            </Menu.Item>
          ) : null}
          {items.includes('settings') ? (
            <Menu.Item
              testID="classroomMenuSettings"
              label={l`Classroom settings`}
              onPress={() => settingsControl.open()}>
              <Menu.ItemText>{l`Classroom settings`}</Menu.ItemText>
              <Menu.ItemIcon icon={SettingsIcon} />
            </Menu.Item>
          ) : null}
          {items.includes('requests') ? (
            <Menu.Item
              testID="classroomMenuRequests"
              label={l`Requests to join`}
              onPress={() => requestsControl.open()}>
              <Menu.ItemText>
                {requestCount > 0
                  ? plural(requestCount, {
                      one: '# request to join',
                      other: '# requests to join',
                    })
                  : l`Requests to join`}
              </Menu.ItemText>
              <Menu.ItemIcon icon={PersonCheckIcon} />
            </Menu.Item>
          ) : null}
          {/* Asking is for the classroom that can be found; a private one takes the code alone. */}
          {items.includes('ask') ? (
            <Menu.Item
              testID="classroomMenuAskToJoin"
              label={l`Ask to join ${classroom.name}`}
              onPress={() => askControl.open()}>
              <Menu.ItemText>{l`Ask to join`}</Menu.ItemText>
              <Menu.ItemIcon icon={PersonPlusIcon} />
            </Menu.Item>
          ) : null}
          {/*
            The code is offered to somebody who is outside the room and not
            already waiting at its door: a person who has asked is offered the
            request they are waiting on instead, which is where the pill above
            puts them (`classroomMenuItems`).
          */}
          {items.includes('code') ? (
            <Menu.Item
              testID="classroomMenuJoinWithCode"
              label={l`Join with a code`}
              onPress={() => joinCodeControl.open()}>
              <Menu.ItemText>{l`Join with a code`}</Menu.ItemText>
              <Menu.ItemIcon icon={LockIcon} />
            </Menu.Item>
          ) : null}
          {/*
            A public room's link is its address and may be copied anywhere. A
            private room's link is its invitation, and that one is **the QR**:
            there is no copyable share link for a room nobody can find, because
            a link in a clipboard is the same secret with the school's own
            picture of who is holding it removed (see `InviteDialog`).
          */}
          {items.includes('copyLink') ? (
            <Menu.Item
              testID="classroomMenuCopyLink"
              label={l`Copy classroom link`}
              onPress={onCopyLink}>
              <Menu.ItemText>{l`Copy classroom link`}</Menu.ItemText>
              <Menu.ItemIcon icon={ChainLinkIcon} />
            </Menu.Item>
          ) : null}
          {/*
            The owner's own page, named for whoever they are: *Primary School* to
            a school's room, *Coach* to a coach's, *Library* to a library's, in the
            words the owner's profile badge and their post bylines use, so the row
            cannot call an account one thing while the app calls it another
            elsewhere (`readOwnerKind`). The icon is the row's own: a building for
            an institution, a person for a creator, drawn in the same line art as
            every other row beside it. A room whose owner this device cannot name
            has no row at all (`classroomMenuItems`).
          */}
          {items.includes('owner') && ownerKind ? (
            <Menu.Item
              testID="classroomMenuSeeOwner"
              label={l`See the ${l(ownerKind.label)}'s profile`}
              onPress={() =>
                navigation.navigate('Profile', {name: classroom.ownerHandle})
              }>
              <Menu.ItemText>{l`See the ${l(ownerKind.label)}`}</Menu.ItemText>
              <Menu.ItemIcon icon={ownerKindIcon(ownerKind.icon)} />
            </Menu.Item>
          ) : null}
        </Menu.Group>

        {items.includes('delete') || items.includes('leave') ? (
          <>
            <Menu.Divider />
            <Menu.Group>
              {items.includes('delete') ? (
                <Menu.Item
                  testID="classroomMenuDelete"
                  label={l`Delete ${classroom.name}`}
                  onPress={onDelete}>
                  <Menu.ItemText>{l`Delete classroom`}</Menu.ItemText>
                  <Menu.ItemIcon icon={TrashIcon} />
                </Menu.Item>
              ) : (
                <Menu.Item
                  testID="classroomMenuLeave"
                  label={l`Leave ${classroom.name}`}
                  onPress={onLeave}>
                  <Menu.ItemText>{l`Leave classroom`}</Menu.ItemText>
                  <Menu.ItemIcon icon={TrashIcon} />
                </Menu.Item>
              )}
            </Menu.Group>
          </>
        ) : null}
      </Menu.Outer>
    </Menu.Root>
  )
}
